import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../lib/supabase";
import Header from '../../components/ui/Header';
import { Loader2, MapPin, Calendar, Users } from "lucide-react";

export default function CustomerMessagesV2EnquiryInbox() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user?.id) {
      fetchEnquiries();
    }
  }, [user]);

  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase?.from("enquiries")?.select("*")?.eq("customer_id", user?.id)?.order("created_at", { ascending: false });

      if (fetchError) throw fetchError;

      setEnquiries(data || []);
    } catch (err) {
      console.error("Error fetching enquiries:", err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEnquiryClick = (enquiryId) => {
    navigate(`/customer/messages-v2/enquiry/${enquiryId}`);
  };

  const handleCreateEnquiry = () => {
    navigate("/create-enquiry");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-4xl mx-auto p-4 md:p-6">
        {/* Header Section */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">My Enquiries</h1>
          <p className="text-gray-600">
            View your travel enquiries and agent offers
          </p>
        </div>

        {/* Error State */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && enquiries?.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No Enquiries Yet
            </h3>
            <p className="text-gray-600 mb-6">
              Start your journey by creating a travel enquiry
            </p>
            <button
              onClick={handleCreateEnquiry}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Create Enquiry
            </button>
          </div>
        )}

        {/* Enquiries List */}
        {enquiries?.length > 0 && (
          <div className="space-y-3">
            {enquiries?.map((enquiry) => (
              <div
                key={enquiry?.id}
                onClick={() => handleEnquiryClick(enquiry?.id)}
                className="bg-white rounded-lg shadow-sm p-4 cursor-pointer hover:shadow-md transition-shadow border border-gray-200"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Destination Title */}
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {enquiry?.destination || "Destination not specified"}
                    </h3>

                    {/* Trip Type Subtitle */}
                    <p className="text-sm text-gray-600 mb-3">
                      {enquiry?.trip_type || "Trip type not specified"}
                    </p>

                    {/* Additional Info */}
                    <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                      {enquiry?.travel_dates && (
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          <span>{enquiry?.travel_dates}</span>
                        </div>
                      )}
                      {(enquiry?.adults || enquiry?.children) && (
                        <div className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          <span>
                            {enquiry?.adults || 0} adults
                            {enquiry?.children ? `, ${enquiry?.children} children` : ""}
                          </span>
                        </div>
                      )}
                      {enquiry?.budget && (
                        <span className="font-medium">
                          Budget: £{enquiry?.budget}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Status Badge */}
                  <div className="ml-4">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        enquiry?.status === "open" ?"bg-green-100 text-green-800"
                          : enquiry?.status === "closed" ?"bg-gray-100 text-gray-800" :"bg-blue-100 text-blue-800"
                      }`}
                    >
                      {enquiry?.status || "pending"}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}